import React, { useEffect, useState, useMemo } from "react";
import { api } from "../lib/api";

const CATEGORY_LABELS = {
  abonnement: "Abonnements toutes les 4 semaines (Quadrihebdomadaire)",
  prive: "Cours privés",
  groupe: "Cours groupe / Drop-in",
  video: "Packs vidéo PowerKick Her",
  evenement: "Événements & camps",
  inscription: "Inscription & Évaluation",
  merch: "Boutique / Merch",
};

const CATEGORY_ICONS = {
  abonnement: "🔁",
  prive: "🥋",
  groupe: "👥",
  video: "🎬",
  evenement: "🎯",
  inscription: "📝",
  merch: "🛍️",
};

function CopyButton({ value, label = "Copier" }) {
  const [copied, setCopied] = useState(false);
  const onClick = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (e) {
      // fallback
      const t = document.createElement("textarea");
      t.value = value;
      document.body.appendChild(t);
      t.select();
      document.execCommand("copy");
      document.body.removeChild(t);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    }
  };
  return (
    <button
      data-testid="stripe-copy-link-btn"
      onClick={onClick}
      className={`px-3 py-1.5 rounded-md text-xs font-semibold transition ${
        copied
          ? "bg-emerald-500 text-zinc-950"
          : "bg-amber-500/10 text-amber-300 border border-amber-500/30 hover:bg-amber-500/20"
      }`}
    >
      {copied ? "✓ Copié" : label}
    </button>
  );
}

export default function StripeManagerModule() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [creatingLinks, setCreatingLinks] = useState(false);
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);

  const fetchCatalogue = async () => {
    try {
      setLoading(true);
      const { data } = await api.get(`/stripe/catalogue`);
      setProducts(data.products || []);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const { data } = await api.get(`/stripe/stats`);
      setStats(data);
    } catch (e) {
      /* non-critical */
    }
  };

  useEffect(() => {
    fetchCatalogue();
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    let rows = products;
    if (filter !== "all") rows = rows.filter((p) => p.category === filter);
    if (search.trim()) {
      const q = search.toLowerCase();
      rows = rows.filter(
        (p) =>
          (p.name || "").toLowerCase().includes(q) ||
          (p.product_key || "").toLowerCase().includes(q),
      );
    }
    return rows;
  }, [products, filter, search]);

  const byCat = useMemo(() => {
    const out = {};
    filtered.forEach((p) => {
      out[p.category] = out[p.category] || [];
      out[p.category].push(p);
    });
    return out;
  }, [filtered]);

  const totalWithLinks = products.filter((p) => !!p.stripe_payment_link).length;

  return (
    <div className="space-y-6" data-testid="stripe-manager-module">
      {/* HEADER */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-amber-400 tracking-tight">
            💳 Stripe Manager · Tous tes liens de paiement
          </h1>
          <p className="text-zinc-400 text-sm mt-1">
            27 produits LIVE sur Stripe · Liens permanents partageables par SMS / Email / QR / Réseaux sociaux
          </p>
        </div>
        <a
          href="https://dashboard.stripe.com/products"
          target="_blank"
          rel="noreferrer"
          data-testid="open-stripe-dashboard-link"
          className="px-4 py-2 rounded-md bg-amber-500 text-zinc-950 font-semibold hover:bg-amber-400 transition"
        >
          🚀 Ouvrir Stripe Dashboard
        </a>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4">
          <div className="text-xs uppercase text-zinc-500">Produits actifs</div>
          <div className="text-2xl font-bold text-amber-300 mt-1">{products.length}</div>
        </div>
        <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4">
          <div className="text-xs uppercase text-zinc-500">Liens de paiement</div>
          <div className="text-2xl font-bold text-emerald-400 mt-1">
            {totalWithLinks}/{products.length}
          </div>
        </div>
        <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4">
          <div className="text-xs uppercase text-zinc-500">Mode</div>
          <div className="text-2xl font-bold text-amber-300 mt-1">
            {stats?.live_mode ? "🔴 LIVE" : "🧪 Test"}
          </div>
        </div>
        <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4">
          <div className="text-xs uppercase text-zinc-500">Sessions checkout</div>
          <div className="text-2xl font-bold text-amber-300 mt-1">
            {stats?.checkout_sessions_total ?? 0}
          </div>
        </div>
      </div>

      {/* FILTERS */}
      <div className="flex flex-wrap items-center gap-2">
        <button
          onClick={() => setFilter("all")}
          data-testid="filter-all-btn"
          className={`px-3 py-1.5 rounded-md text-sm font-semibold transition ${
            filter === "all"
              ? "bg-amber-500 text-zinc-950"
              : "bg-zinc-900 text-zinc-300 border border-zinc-800 hover:border-amber-500/40"
          }`}
        >
          Tous ({products.length})
        </button>
        {Object.entries(CATEGORY_LABELS).map(([k, lbl]) => {
          const count = products.filter((p) => p.category === k).length;
          if (!count) return null;
          return (
            <button
              key={k}
              onClick={() => setFilter(k)}
              data-testid={`filter-${k}-btn`}
              className={`px-3 py-1.5 rounded-md text-sm font-semibold transition ${
                filter === k
                  ? "bg-amber-500 text-zinc-950"
                  : "bg-zinc-900 text-zinc-300 border border-zinc-800 hover:border-amber-500/40"
              }`}
            >
              {CATEGORY_ICONS[k]} {lbl} ({count})
            </button>
          );
        })}
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="🔍 Recherche par nom…"
          data-testid="stripe-search-input"
          className="flex-1 min-w-[180px] px-3 py-1.5 bg-zinc-900 border border-zinc-800 rounded-md text-sm text-zinc-200 placeholder-zinc-500 focus:outline-none focus:border-amber-500/50"
        />
      </div>

      {/* PRODUCTS GROUPED BY CATEGORY */}
      {loading && <div className="text-zinc-400">Chargement…</div>}
      {error && <div className="text-rose-400">⚠️ {error}</div>}
      {!loading &&
        Object.keys(byCat).length > 0 &&
        Object.entries(byCat).map(([cat, rows]) => (
          <section key={cat} className="space-y-3">
            <h2 className="text-lg font-semibold text-amber-300 flex items-center gap-2">
              <span>{CATEGORY_ICONS[cat] || "•"}</span>
              <span>{CATEGORY_LABELS[cat] || cat}</span>
              <span className="text-xs text-zinc-500 font-normal">({rows.length})</span>
            </h2>
            <div className="grid md:grid-cols-2 gap-3">
              {rows.map((p) => (
                <div
                  key={p.product_key}
                  className="bg-zinc-900 border border-zinc-800 rounded-lg p-4 hover:border-amber-500/30 transition"
                  data-testid={`stripe-product-card-${p.product_key}`}
                >
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-zinc-100 truncate">{p.name}</div>
                      <div className="text-xs text-zinc-500 mt-0.5">{p.product_key}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-xl font-bold text-amber-300">
                        {p.price_cad?.toFixed(2)}$
                      </div>
                      {p.mode === "subscription" && (
                        <div className="text-[10px] uppercase text-emerald-400">/ mois</div>
                      )}
                    </div>
                  </div>
                  {p.description && (
                    <p className="text-xs text-zinc-400 mb-3 line-clamp-2">{p.description}</p>
                  )}

                  {p.stripe_payment_link ? (
                    <div className="space-y-2">
                      <div className="bg-zinc-950 border border-zinc-800 rounded px-2 py-1.5 text-[11px] font-mono text-amber-300/90 truncate">
                        {p.stripe_payment_link}
                      </div>
                      <div className="flex gap-2 flex-wrap">
                        <CopyButton value={p.stripe_payment_link} label="📋 Copier lien" />
                        <a
                          href={p.stripe_payment_link}
                          target="_blank"
                          rel="noreferrer"
                          data-testid={`open-link-${p.product_key}`}
                          className="px-3 py-1.5 rounded-md bg-zinc-800 text-zinc-200 text-xs font-semibold hover:bg-zinc-700 transition"
                        >
                          🔗 Ouvrir
                        </a>
                        <a
                          href={`sms:?&body=${encodeURIComponent(`${p.name} — ${p.price_cad?.toFixed(2)}$ CAD\n${p.stripe_payment_link}`)}`}
                          data-testid={`sms-${p.product_key}`}
                          className="px-3 py-1.5 rounded-md bg-zinc-800 text-zinc-200 text-xs font-semibold hover:bg-zinc-700 transition"
                        >
                          📱 SMS
                        </a>
                        <a
                          href={`mailto:?subject=${encodeURIComponent(p.name)}&body=${encodeURIComponent(`Bonjour,\n\nVoici le lien sécurisé pour ${p.name} — ${p.price_cad?.toFixed(2)}$ CAD :\n${p.stripe_payment_link}\n\nMerci !\nKru Ralph`)}`}
                          data-testid={`email-${p.product_key}`}
                          className="px-3 py-1.5 rounded-md bg-zinc-800 text-zinc-200 text-xs font-semibold hover:bg-zinc-700 transition"
                        >
                          📧 Email
                        </a>
                      </div>
                    </div>
                  ) : (
                    <div className="text-xs text-rose-400">⚠️ Pas encore de lien généré</div>
                  )}
                </div>
              ))}
            </div>
          </section>
        ))}

      {/* GMAIL DRAFTS REPORT */}
      <section className="bg-zinc-900 border border-zinc-800 rounded-lg p-5 mt-8">
        <h2 className="text-lg font-semibold text-amber-300 mb-2">
          📬 Brouillons Gmail générés par IA (Camille)
        </h2>
        <p className="text-sm text-zinc-400 mb-3">
          9 brouillons de réponse créés à partir de tes derniers emails (Hatashita, Hayabusa, Tatami,
          Capital Conquest, LePointDeVente, Tanya, Adrien, Oscar, Kamryn/Leandata).
        </p>
        <a
          href="https://mail.google.com/mail/u/impactkb.bjj@gmail.com/#drafts"
          target="_blank"
          rel="noreferrer"
          data-testid="open-gmail-drafts-link"
          className="inline-block px-4 py-2 rounded-md bg-amber-500 text-zinc-950 font-semibold hover:bg-amber-400 transition"
        >
          📂 Ouvrir mon dossier Brouillons Gmail
        </a>
      </section>
    </div>
  );
}
